<template>
  <div>
    <h1>Manage Request Statuses</h1>

    <b-modal size="xl" id="modal-crud" hide-header hide-footer>
      <h2>{{formTitle}}</h2>
      <hr/>
      <form ref="form" @submit.prevent="save">
            <label><strong>Help Prompt for Status:</strong></label>
            <quill-editor
                ref="myQuillEditor"
                v-model="$v.editedItem.helpPrompt.$model"
                :options="editorOptions"
            />

          <b-form-group 
            label="Responsibility" label-for="resp-input" 
            invalid-feedback="Request Status Responsibility is required">
            <b-form-input id="resp-input" 
              v-model="$v.editedItem.responsible.$model" 
              :state="$v.editedItem.responsible.$dirty ? !$v.editedItem.responsible.$error : null"
              aria-describedby="input-1-live-feedback"
              required>
            </b-form-input> 
            <b-form-invalid-feedback id="input-1-live-feedback">
              This is a required field and must have a length of 75 characters or less.
            </b-form-invalid-feedback>     
            <b-form-text id="name-input-help">Required.</b-form-text> 
          </b-form-group>
          
          <b-button @click="close" style="margin-right:15px;">Cancel</b-button>
          <b-button type="submit" variant="primary" :disabled="$v.editedItem.$invalid">Save</b-button>
        </form>
    </b-modal>

    <b-table hover small responsive show-empty
      :fields="fields"
      :items="items"
      :busy="isLoading">
      <template v-slot:cell(actions)="row">
        <b-button variant="primary" size="sm" @click="editItem(row.item)" class="mr-1">
          edit
        </b-button>
      </template>
      <template #table-caption></template>
      <template v-slot:no-data>
        <v-btn color="primary" @click="initialize">Reset</v-btn>
      </template>

      <template v-slot:empty="scope">
        <p>No request statuses found.</p>
      </template>

      <template v-slot:cell(active)="data">
        {{ data.item.active ? 'Yes' : 'No' }}
      </template>

      <div slot="table-busy" class="text-center text-danger my-2">
          <b-spinner class="align-middle" style="margin-right:5px; margin-top:12px;"></b-spinner>
          <strong>Loading, Please Wait...</strong>
      </div>        

    </b-table>
  </div>
</template>

<script>
  import axios from 'axios';
  import axiosRetry from 'axios-retry';
  import { validationMixin } from 'vuelidate'
  import pageTitle from '@/mixins/pageTitle'
  import { required, maxLength } from 'vuelidate/lib/validators'
  import isRequestTypeUnique from '@/validators/isRequestTypeUnique.js'
  import 'quill/dist/quill.core.css'
  import 'quill/dist/quill.snow.css'
  import { quillEditor } from 'vue-quill-editor'

  axiosRetry(axios, { retries: 3,
                      retryDelay: axiosRetry.exponentialDelay  });

  export default {
    title: 'Manage Request Statuses',
    mixins: [validationMixin, pageTitle],
    components: {
      quillEditor
    },

    data: () => ({
      fields: [
        {key: 'code', sortable: true, label: 'Request Status Code'},
        {key: 'description', sortable: true, label: 'Request Status Description'},
        {key: 'responsible', sortable: true, label: 'Responsiblity'},
        {key: 'actions', label: 'Actions'}
      ],
      isLoading: false,
      items: [],
      showInactive: false,
      editedIndex: -1,
      editedItem: {
        code: null,
        helpPrompt: null,
        responsible: null,
        active: true,
      },
      defaultItem: {
        code: '',
        helpPrompt: '',
        responsible: '',
        active: true,
      },
      editorOptions: {
        theme: 'snow',
        boundary: document.body,
        modules: {
        toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote', 'code-block'],
            [{ 'header': 1 }, { 'header': 2 }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'script': 'sub' }, { 'script': 'super' }],
            [{ 'indent': '-1' }, { 'indent': '+1' }],
            [{ 'direction': 'rtl' }],
            [{ 'size': ['small', false, 'large', 'huge'] }],
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'font': [] }],
            [{ 'align': [] }],
            ['clean']
        ]
        },
        placeholder: 'Insert help prompts here for this status...',
        readOnly: false
      }
    }),

    validations() {
        return {
          editedItem: {
            responsible: {
              required,
              maxLength: maxLength(75),
            },
            helpPrompt: {
              maxLength: maxLength(4000),
            },
            active: { }
          }
        }
    },

    computed: {
      formTitle () {
        return this.editedIndex === -1 ? 'New Request Type' : 'Edit Request Type'
      }
    },

    watch: {
      items() {
        this.isLoading = false
      },
    },

    created () {
      this.initialize()
    },

    methods: {
      fetchItems() {
          this.isLoading = true;
          axios.get(`${process.env.VUE_APP_API_BASE_URL}/meta/statuses/full`)
            .then(response => {
                this.items = response.data;
                this.isLoading = false;
            })
      },

      initialize () {
        this.fetchItems()
      },

      editItem (item) {
        this.editedIndex = this.items.indexOf(item)
        this.editedItem = Object.assign({}, item)
        this.$bvModal.show('modal-crud')
      },

      close () {
        this.$bvModal.hide('modal-crud')
        setTimeout(() => {
          this.editedItem = Object.assign({}, this.defaultItem)
          this.editedIndex = -1
          this.$v.$reset()
          this.fetchItems()
        }, 300)
      },

      save () {
        axios.put(`${process.env.VUE_APP_API_BASE_URL}/requeststatus`, this.editedItem)
        .then(response => { 
            this.items = response.data;
            this.$bvToast.toast('Request Status Updated', {
              title: 'Status',
              toaster: 'b-toaster-top-center',
              solid: true,
              variant: 'success',
              appendToast: true
            }) 
        });
        this.close()
      },
    }
  }
</script>